import type { ApiError } from '../lib/types'
import { RequestFailed } from '../lib/api'

type Tone = 'info' | 'ok' | 'warn' | 'danger' | 'synthetic'

const GLYPH: Record<Tone, string> = {
  info: 'i',
  ok: '✓',
  warn: '!',
  danger: '×',
  synthetic: '▲',
}

export function Callout({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: Tone
  title?: string
  children?: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <div className={`callout ${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <span className="icon" aria-hidden>
        {GLYPH[tone]}
      </span>
      <div className="body">
        {title ? <strong>{title}</strong> : null}
        {title && children ? <br /> : null}
        {children}
      </div>
      {actions}
    </div>
  )
}

/**
 * Error surface.
 *
 * Domain errors carry a machine code and an actionable message, so they are
 * shown as they are rather than replaced with a generic apology.
 */
export function ErrorCallout({
  error,
  onRetry,
}: {
  error: unknown
  onRetry?: () => void
}) {
  const payload: ApiError =
    error instanceof RequestFailed
      ? error.error
      : {
          code: 'unexpected_error',
          message: error instanceof Error ? error.message : 'Something went wrong.',
        }
  const details = payload.details as any
  return (
    <Callout
      tone="danger"
      title={payload.message}
      actions={
        onRetry ? (
          <button className="small" onClick={onRetry}>
            Retry
          </button>
        ) : undefined
      }
    >
      <div className="tiny mono faint">{payload.code}</div>
      {Array.isArray(details?.clarifications) && details.clarifications.length > 0 ? (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {details.clarifications.map((c: any, i: number) => (
            <li key={i}>{c.question}</li>
          ))}
        </ul>
      ) : null}
      {Array.isArray(details?.issues) && details.issues.length > 0 ? (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {details.issues.map((issue: any, i: number) => (
            <li key={i}>
              {issue.message}
              {issue.guidance ? <span className="faint"> {issue.guidance}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {Array.isArray(details?.blocking_issues) && details.blocking_issues.length > 0 ? (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {details.blocking_issues.map((issue: string, i: number) => (
            <li key={i}>{issue}</li>
          ))}
        </ul>
      ) : null}
    </Callout>
  )
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string
  children?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children ? <div className="small muted">{children}</div> : null}
      {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
    </div>
  )
}
